import { useCallback, useMemo, useRef } from 'react';

import { useFocusEffect } from '@react-navigation/native';

import { URLS } from '@/data';
import { getCachedAppInstanceId } from '@/feature/analytics/appInstanceId';
import { productNames } from '@/hooks/use-active-product-name';
import { Jurisdiction, Product, useJurisdictionStore } from '@/hooks/use-jurisdiction';
import { useResumeEffect } from '@/hooks/use-resume';
import { useUserStore } from '@/hooks/use-user-store';
import { GlobalConfigurationsDocumentDataBodyKeyValuePairsSlice } from '@/types/prismic.generated';
import { Wallet, getWalletBalances } from '@/utils/fetch-wallet-balance';
import { logger } from '@/utils/logging';
import { toFraction } from '@/utils/numeric/fraction';
import { useQuery } from '@tanstack/react-query';
import { create } from 'zustand';

import { SocialCurrency, useSocialCurrencyStore } from './use-social-currency';

export const defaultBalances = {
    total: '0',
    real_amount: '0',
    bonus_amount: '0',
    free_amount: '0',
    real_currency: '0',
};

type ActiveWalletStore = {
    wallets: ProductWallet;
    activeWalletProduct: Product;
    loadWallets: () => void;
    setActiveWallet: (product: Product, jurisdiction: string) => void;
    jurisdiction: string;
    resetActiveWallet: () => void;
    walletLoading: boolean;
    walletDefaultCurrency: Record<Product, ProductCurrencies>;
};

export type ProductCurrencies = SocialCurrency.COINS | SocialCurrency.CASH | 'USD';

type ProductWallet = {
    [key in Product]: Partial<{
        [currency in ProductCurrencies]: Wallet;
    }>;
};

export const useActiveWalletStore = create<ActiveWalletStore>((set, get) => ({
    wallets: {
        [Product.Pickem]: {
            USD: defaultBalances,
        },
        [Product.Arcade]: {
            USD: defaultBalances,
        },
        [Product.Sweeps]: {
            COINS: defaultBalances,
            CASH: defaultBalances,
        },
        [Product.Casino]: {
            COINS: defaultBalances,
            CASH: defaultBalances,
        },
        [Product.Prediction]: {
            USD: defaultBalances,
        },
        [Product.Rips]: {
            USD: defaultBalances,
        },
        [Product.None]: {
            USD: defaultBalances,
        },
    },
    walletDefaultCurrency: {
        [Product.Pickem]: 'USD',
        [Product.Arcade]: 'USD',
        [Product.Sweeps]: SocialCurrency.CASH,
        [Product.Casino]: SocialCurrency.CASH,
        [Product.Prediction]: 'USD',
        [Product.Rips]: 'USD',
        [Product.None]: 'USD',
    },
    walletAvailableCurrencies: {
        [Product.Pickem]: ['USD'],
        [Product.Arcade]: 'USD',
        [Product.Sweeps]: ['COINS', 'CASH'],
        [Product.Prediction]: ['USD'],
        [Product.Rips]: ['USD'],
        [Product.None]: ['USD'],
    },
    activeWalletProduct: Product.None,
    setActiveWallet: (product: Product, jurisdiction: string) => {
        set({ activeWalletProduct: product, jurisdiction });
    },
    loadWallets: async () => {
        if (get().walletLoading) {
            return;
        }
        const { actions, guest } = useUserStore.getState();
        if (actions.sessionHasExpired() || guest) {
            logger.debug('[wallet] No session or guest user, not loading wallet.');
            return;
        }
        set({ walletLoading: true });
        try {
            const data = await getWalletBalances();
            set({
                walletLoading: false,
                wallets: {
                    [Product.Pickem]: {
                        USD: data.fantasy_wallet,
                    },
                    [Product.Arcade]: {
                        USD: data.fantasy_wallet, // arcade and pickem share fantasy_wallet
                    },
                    [Product.Sweeps]: {
                        COINS: data.sweeps_coin_wallet,
                        CASH: data.sweeps_cash_wallet,
                    },
                    [Product.Casino]: {
                        COINS: data.sweeps_coin_wallet,
                        CASH: data.sweeps_cash_wallet,
                    },
                    [Product.Prediction]: {
                        USD: data.fantasy_wallet, // prediction markets share the fantasy_wallet
                    },
                    [Product.Rips]: {
                        USD: data.fantasy_wallet, // rips shares the fantasy_wallet
                    },
                    [Product.None]: {
                        USD: defaultBalances,
                    },
                },
            });
        } catch (e) {
            logger.error('[wallet] Error loading user wallet', e);
            set({ walletLoading: false });
        }
    },
    jurisdiction: '',
    resetActiveWallet: () => {
        set({ activeWalletProduct: Product.None });
    },
    walletLoading: false,
}));

useSocialCurrencyStore.subscribe((state, prevState) => {
    if (state.activeCurrency !== prevState.activeCurrency) {
        const { activeWalletProduct, setActiveWallet } = useActiveWalletStore.getState();
        // Only update if we're on a sweeps or casino product
        if (activeWalletProduct === Product.Sweeps || activeWalletProduct === Product.Casino) {
            const isCasinoCurrency = activeWalletProduct === Product.Casino;
            const newJurisdiction =
                state.activeCurrency === SocialCurrency.COINS
                    ? isCasinoCurrency
                        ? Jurisdiction.CASINOCOIN
                        : Jurisdiction.SWEEPSCOIN
                    : isCasinoCurrency
                    ? Jurisdiction.CASINOCASH
                    : Jurisdiction.SWEEPSCASH;
            setActiveWallet(activeWalletProduct, newJurisdiction);
        }
    }
});

type WalletLoadOptions = {
    refetchOnResume: boolean;
};

export type ActiveWallet = {
    wallet: Partial<{
        COINS: {
            total: string;
            real_amount: string;
            bonus_amount: string;
            free_amount: string;
            real_currency: string;
            next_top_up?: number | undefined;
        };
        CASH: {
            total: string;
            real_amount: string;
            bonus_amount: string;
            free_amount: string;
            real_currency: string;
            next_top_up?: number | undefined;
        };
        USD: {
            total: string;
            real_amount: string;
            bonus_amount: string;
            free_amount: string;
            real_currency: string;
            next_top_up?: number | undefined;
        };
    }>;
    balance: number;
    total: number;
    bonus: number;
    betrBucks: number;
    activeWalletProductName: string;
    activeWalletProduct: Product;
    wallets: ProductWallet;
    walletLoading: boolean;
    isSharedSocialWallet: boolean;
};

/**
 * Main hook to fetch all the data for the current active wallet in the Account screen
 */
export const useActiveWallet = (opts?: WalletLoadOptions): ActiveWallet => {
    //Flag indicating if we loaded at least once the wallet
    const initiallyFetched = useRef(false);
    //Flag indicating if we should reload the wallet when resuming
    const refetchOnResume = opts ? opts.refetchOnResume : true;

    useInitActiveWallet();
    const loadWallets = useActiveWalletStore(state => state.loadWallets);
    const wallets = useActiveWalletStore(state => state.wallets);
    const activeWalletProduct = useActiveWalletStore(state => state.activeWalletProduct);
    const walletLoading = useActiveWalletStore(state => state.walletLoading);
    const walletDefaultCurrency = useActiveWalletStore(state => state.walletDefaultCurrency);
    useResumeEffect(
        useCallback(() => {
            if (refetchOnResume || !initiallyFetched.current) {
                loadWallets();
                initiallyFetched.current = true;
            }
        }, [loadWallets, refetchOnResume])
    );

    const defaultCurrency = walletDefaultCurrency[activeWalletProduct];
    const activeWallet = wallets[activeWalletProduct][defaultCurrency] || defaultBalances;

    return {
        wallet: wallets[activeWalletProduct],
        balance: toFraction(activeWallet.real_amount),
        total: toFraction(activeWallet.total),
        bonus: toFraction(activeWallet.bonus_amount),
        betrBucks: toFraction(activeWallet.free_amount),
        activeWalletProductName: productNames[activeWalletProduct],
        activeWalletProduct,
        wallets,
        walletLoading,
        isSharedSocialWallet: activeWalletProduct === Product.Sweeps || activeWalletProduct === Product.Casino,
    };
};

/**
 * Creates the jurisdiction headers for the active wallet
 */
export const createActiveWalletJurisdictionHeaders = () => {
    const accessToken = useUserStore.getState().session?.access_token;
    const jurisdiction = useActiveWalletStore.getState().jurisdiction;
    const userState = useJurisdictionStore.getState().jurisdiction;
    const appInstanceId = getCachedAppInstanceId();

    const headers: { [key: string]: string } = {
        JURISDICTION: jurisdiction,
        Authorization: `Bearer ${accessToken}`,
    };

    if (appInstanceId) headers['X-App-Instance-Id'] = appInstanceId;

    // Cloudflare state override based on user detected location
    if (userState) headers['X-CUSTOM-REGION-CODE'] = userState;

    return headers;
};

/**
 * Creates jurisdiction headers for wallet operations,
 * mapping Sweeps and Casino products to their cash-only jurisdictions
 */
export const createActiveCashWalletHeaders = () => {
    const baseHeaders = createActiveWalletJurisdictionHeaders();
    const product = useActiveWalletStore.getState().activeWalletProduct;

    if (product === Product.Sweeps) {
        return {
            ...baseHeaders,
            jurisdiction: Jurisdiction.SWEEPSCASH,
        };
    }

    if (product === Product.Casino) {
        return {
            ...baseHeaders,
            jurisdiction: Jurisdiction.CASINOCASH,
        };
    }

    return baseHeaders;
};

/**
 * Hook that creates the jurisdiction headers for the active wallet
 */
export const useActiveWalletJurisdictionHeaders = () => {
    const session = useUserStore(state => state.session);
    const jurisdiction = useActiveWalletStore(state => state.jurisdiction);
    const userState = useJurisdictionStore(state => state.jurisdiction);

    return useMemo(() => {
        const appInstanceId = getCachedAppInstanceId();
        const headers: { [key: string]: string } = {
            JURISDICTION: jurisdiction,
            Authorization: `Bearer ${session?.access_token}`,
        };

        if (appInstanceId) {
            headers['X-App-Instance-Id'] = appInstanceId;
        }

        // Add custom region header if user state is available
        if (userState) {
            headers['X-CUSTOM-REGION-CODE'] = userState;
        }

        return headers;
    }, [jurisdiction, session?.access_token, userState]);
};

/**
 * Sets the active wallet to the incoming product/jurisdiction
 */
const useInitActiveWallet = () => {
    const switchActiveWallet = useSwitchActiveWallet();

    const jurisdiction = useJurisdictionStore(state => state.jurisdiction);
    const product = useJurisdictionStore(state => state.product);
    const activeWalletProduct = useActiveWalletStore(state => state.activeWalletProduct);

    useFocusEffect(
        useCallback(() => {
            if (jurisdiction && (activeWalletProduct === Product.None || activeWalletProduct !== product)) {
                switchActiveWallet(product);
            }
        }, [jurisdiction, activeWalletProduct, switchActiveWallet, product])
    );
};

/**
 * Fetches the jurisdiction settings for the active wallet
 * @param jurisdiction the jurisdiction to fetch settings for
 */
const fetchJurisdictionSettings = async (jurisdiction: string) => {
    const resp = await fetch(`${URLS.ACS_URL}/${jurisdiction.toLowerCase()}`);
    if (!resp.ok) {
        throw new Error('Error fetching jurisdiction settings for active wallet');
    }
    return await resp.json();
};

/**
 * Selector function to fetch API keys from the ACS response
 * @param data response from ACS
 */
const apiKeySelector = (data: any) => {
    return data?.data?.default_product?.data?.body
        ?.find((it: GlobalConfigurationsDocumentDataBodyKeyValuePairsSlice) => it.primary.name === 'API keys')
        ?.items?.reduce((acc: Record<string, string>, currentValue: { key: string; value: string }) => {
            return { ...acc, [currentValue.key]: currentValue.value };
        }, {});
};

/**
 * Hook that fetches the paysafe api keys for the active wallet
 */
export const useActiveWalletPaysafeCredentials = () => {
    const jurisdiction = useActiveWalletStore(state => state.jurisdiction);
    return useQuery({
        queryKey: ['paysafe-keys', jurisdiction],
        queryFn: () => fetchJurisdictionSettings(jurisdiction),
        select: apiKeySelector,
        staleTime: Infinity,
        enabled: !!jurisdiction,
        retry: 3,
        retryDelay: 1000,
    });
};

/**
 * Hook that switches the active wallet
 */
export const useSwitchActiveWallet = () => {
    const setActiveWallet = useActiveWalletStore(state => state.setActiveWallet);

    return useCallback(
        (product: Product) => {
            switch (product) {
                case Product.Pickem:
                    setActiveWallet(Product.Pickem, Jurisdiction.FANTASY);
                    break;
                case Product.Sweeps:
                    const socialCurrency = useSocialCurrencyStore.getState().activeCurrency;
                    const sweepsJurisdiction =
                        socialCurrency === SocialCurrency.COINS ? Jurisdiction.SWEEPSCOIN : Jurisdiction.SWEEPSCASH;
                    setActiveWallet(product, sweepsJurisdiction);
                    break;
                case Product.Casino:
                    const casinoCurrency = useSocialCurrencyStore.getState().activeCurrency;
                    const casinoJurisdiction =
                        casinoCurrency === SocialCurrency.COINS ? Jurisdiction.CASINOCOIN : Jurisdiction.CASINOCASH;
                    setActiveWallet(product, casinoJurisdiction);
                    break;
                case Product.Arcade:
                    setActiveWallet(Product.Arcade, Jurisdiction.ARCADE);
                    break;
                case Product.Prediction:
                    setActiveWallet(Product.Prediction, Jurisdiction.PREDICTION);
                    break;
                case Product.Rips:
                    setActiveWallet(Product.Rips, Jurisdiction.RIPS);
                    break;
                default:
                    break;
            }
        },
        [setActiveWallet]
    );
};
